import { API } from '@/config';

export type ApiPagination = {
  total: number;
  page: number;
  pages: number;
  per_page: number;
};

export type ApiErrorBody = {
  message: string;
  code: number;
};

export type ApiEnvelope<T> = {
  ok: boolean;
  data?: T;
  error?: ApiErrorBody;
  pagination?: ApiPagination;
};

export type InfoboxItem = {
  key: string;
  value: string;
};

export type CoverHero = {
  title?: string;
  kicker?: string;
  image?: string;
};

export type Infobox = {
  items: InfoboxItem[];
  cover?: CoverHero;
  metadata?: Record<string, unknown>;
};

export type WikiLink = {
  target: string;
  slug?: string;
  label: string;
  resolved: boolean;
};

export type Heading = {
  level: number;
  text: string;
  id: string;
};

export type TranslationLink = {
  language: string;
  page_id: string;
  slug: string;
  title: string;
};

export type PageSummary = {
  id: string;
  slug: string;
  title: string;
  language: string;
  translation_group?: string;
  current_revision?: string;
  media_urls: string[];
  categories?: string[];
  is_stub: boolean;
  created_at: string;
  updated_at: string;
};

export type CategorySummary = {
  name: string;
  count: number;
};

export type CategoryPage = {
  name: string;
  pages: PageSummary[];
  next_cursor?: string;
};

export type PageList = {
  pages: PageSummary[];
  next_cursor?: string;
};

export type PageRead = PageSummary & {
  categories?: string[];
  html?: string;
  markdown?: string;
  infobox: Infobox;
  links: WikiLink[];
  headings: Heading[];
  translations: TranslationLink[];
  published_at?: string;
  published_by?: string;
  author?: AuthorRef;
  redirected_from?: string;
  redirect_loop?: boolean;
};

export type GraphNode = {
  id: string;
  page_id?: string;
  slug?: string;
  title: string;
  missing: boolean;
  stub: boolean;
  outbound_count: number;
  inbound_count: number;
  categories?: string[];
};

export type GraphEdge = {
  from: string;
  to: string;
};

export type WikiGraph = {
  nodes: GraphNode[];
  edges: GraphEdge[];
  adjacency: Record<string, string[]>;
  backlinks: Record<string, string[]>;
};

export type PageGraph = {
  center: GraphNode;
  graph: WikiGraph;
  backlinks: GraphNode[];
  relevant?: GraphNode[];
};

export type ReportKind = 'wanted' | 'orphans' | 'dead-ends';

export type ReportNode = {
  id: string;
  page_id?: string;
  slug?: string;
  title: string;
  missing: boolean;
  stub: boolean;
  outbound_count: number;
  inbound_count: number;
};

export type ReportResponse = {
  kind: string;
  nodes: ReportNode[];
  total: number;
};

export type SiteInfo = {
  main_page_slug: string;
  // main_pages maps a content language to that language's main page slug (from the
  // main page translation group), so the SPA can resolve the home page per language.
  main_pages?: Record<string, string>;
  // main_page_lang is the language the configured main page actually exists in; the
  // SPA falls back to it for the home when the requested language has no main page.
  main_page_lang?: string;
  site_title: string;
};

export type AuthorRef = {
  id: string;
  name: string;
  email?: string;
};

export type RevisionSummary = {
  id: string;
  page_id: string;
  slug: string;
  title: string;
  summary?: string;
  published_by: string;
  author?: AuthorRef;
  published_at: string;
};

export type RevisionRead = RevisionSummary & {
  markdown?: string;
  rendered_html?: string;
};

export type RevisionListResponse = {
  revisions: RevisionSummary[];
  next_cursor?: string;
};

export type RecentChange = {
  page_id: string;
  revision_id: string;
  slug: string;
  title: string;
  language: string;
  published_at: string;
  published_by: string;
  author?: AuthorRef;
};

export type RecentChangesResponse = {
  changes: RecentChange[];
  next_before?: string;
};

export type RecentChangesQuery = {
  limit?: number;
  before?: string;
  lang?: string;
};

export type PageQuery = {
  limit?: number;
  cursor?: string;
  prefix?: string;
};

export type SearchQuery = PageQuery & {
  q: string;
};

export class WikiApiError extends Error {
  readonly status: number;
  readonly code?: number;

  constructor(message: string, status: number, code?: number) {
    super(message);
    this.name = 'WikiApiError';
    this.status = status;
    this.code = code;
  }
}

export type SiteStats = {
  published_pages: number;
  stub_pages: number;
  total_revisions: number;
  images: number;
  categories: number;
  accounts_by_role: Record<string, number>;
};

export type PageInfo = {
  id: string;
  slug: string;
  title: string;
  language: string;
  translation_group?: string;
  current_revision?: string;
  revision_count: number;
  contributors: AuthorRef[];
  inbound_count: number;
  outbound_count: number;
  categories: string[];
  created_at: string;
  updated_at: string;
  translations: TranslationLink[];
};

export type Notification = {
  id: string;
  kind: string;
  target_type: string;
  target_id: string;
  summary: string;
  read: boolean;
  created_at: string;
};

export type NotificationListResponse = {
  notifications: Notification[];
  unread_count: number;
};

export type WikiApiClient = {
  listPages(lang: string, query?: PageQuery): Promise<PageList>;
  searchPages(lang: string, query: SearchQuery): Promise<PageList>;
  getPage(lang: string, slug: string, opts?: { redirectNo?: boolean }): Promise<PageRead>;
  getWikiGraph(): Promise<WikiGraph>;
  getPageGraph(lang: string, slug: string): Promise<PageGraph>;
  getSite(): Promise<SiteInfo>;
  listCategories(lang: string): Promise<CategorySummary[]>;
  getCategory(lang: string, name: string, query?: PageQuery): Promise<CategoryPage>;
  listRevisions(lang: string, slug: string, query?: PageQuery): Promise<RevisionListResponse>;
  getRevision(id: string): Promise<RevisionRead>;
  getRecentChanges(query?: RecentChangesQuery): Promise<RecentChangesResponse>;
  getReport(kind: ReportKind, lang?: string): Promise<ReportResponse>;
  randomPage(lang: string, query?: { category?: string }): Promise<PageSummary>;
  getPageInfo(lang: string, slug: string): Promise<PageInfo>;
  getStats(): Promise<SiteStats>;
  getNotifications(token: string): Promise<NotificationListResponse>;
  markNotificationRead(id: string, token: string): Promise<void>;
  markAllNotificationsRead(token: string): Promise<void>;
};

export function createWikiApiClient(baseURL = API.wikiBase): WikiApiClient {
  const normalizedBaseURL = baseURL.replace(/\/+$/, '');

  return {
    listPages(lang, query) {
      return request<PageList>(`${normalizedBaseURL}/${lang}/pages${queryString(query)}`);
    },
    searchPages(lang, query) {
      return request<PageList>(`${normalizedBaseURL}/${lang}/search${queryString(query)}`);
    },
    getPage(lang, slug, opts) {
      const suffix = opts?.redirectNo ? '?redirect=no' : '';
      return request<PageRead>(
        `${normalizedBaseURL}/${lang}/pages/${encodeURIComponent(slug)}${suffix}`,
      );
    },
    getWikiGraph() {
      return request<WikiGraph>(`${normalizedBaseURL}/graph`);
    },
    getPageGraph(lang, slug) {
      return request<PageGraph>(
        `${normalizedBaseURL}/${lang}/pages/${encodeURIComponent(slug)}/graph`,
      );
    },
    getSite() {
      return request<SiteInfo>(`${normalizedBaseURL}/site`);
    },
    async listCategories(lang) {
      const payload = await request<{ categories: CategorySummary[] }>(
        `${normalizedBaseURL}/${lang}/categories`,
      );
      return payload.categories;
    },
    async getCategory(lang, name, query) {
      const list = await request<PageList>(
        `${normalizedBaseURL}/${lang}/categories/${encodeURIComponent(name)}${queryString(query)}`,
      );
      return { name, ...list };
    },
    listRevisions(lang, slug, query) {
      return request<RevisionListResponse>(
        `${normalizedBaseURL}/${lang}/pages/${encodeURIComponent(slug)}/revisions${queryString(query)}`,
      );
    },
    getRevision(id) {
      return request<RevisionRead>(`${normalizedBaseURL}/revisions/${encodeURIComponent(id)}`);
    },
    getRecentChanges(query) {
      return request<RecentChangesResponse>(
        `${normalizedBaseURL}/recent-changes${queryString(query)}`,
      );
    },
    getReport(kind, lang) {
      const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
      return request<ReportResponse>(
        `${normalizedBaseURL}/reports/${encodeURIComponent(kind)}${qs}`,
      );
    },
    randomPage(lang, query) {
      const qs = query?.category ? `?category=${encodeURIComponent(query.category)}` : '';
      return request<PageSummary>(`${normalizedBaseURL}/${lang}/random${qs}`);
    },
    getPageInfo(lang, slug) {
      return request<PageInfo>(
        `${normalizedBaseURL}/${lang}/pages/${encodeURIComponent(slug)}/info`,
      );
    },
    getStats() {
      return request<SiteStats>(`${normalizedBaseURL}/stats`);
    },
    getNotifications(token) {
      return request<NotificationListResponse>(`${normalizedBaseURL}/notifications`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    },
    markNotificationRead(id, token) {
      return request<void>(`${normalizedBaseURL}/notifications/${encodeURIComponent(id)}/read`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    },
    markAllNotificationsRead(token) {
      return request<void>(`${normalizedBaseURL}/notifications/read-all`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    },
  };
}

async function request<T>(input: string, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    headers: {
      Accept: 'application/json',
      ...init?.headers,
    },
    ...init,
  });

  const envelope = (await response.json()) as ApiEnvelope<T>;

  if (!response.ok || !envelope.ok) {
    throw new WikiApiError(
      envelope.error?.message ?? response.statusText,
      response.status,
      envelope.error?.code,
    );
  }

  if (envelope.data === undefined) {
    throw new WikiApiError('response data is missing', response.status);
  }

  return envelope.data;
}

function queryString(query?: PageQuery | SearchQuery): string {
  if (!query) {
    return '';
  }

  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') {
      continue;
    }

    params.set(key, String(value));
  }

  const serialized = params.toString();

  return serialized ? `?${serialized}` : '';
}

export const wikiApiClient = createWikiApiClient();
